import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatPrice, convertPrice, convertToGBP } from '../utils/currencyUtils';
import { supabase } from '../supabase';
import StripePayment from '../components/StripePayment';
import ErrorBoundary from '../components/ErrorBoundary';
import './Checkout.css';

// Helper function to get currency symbol for a specific country
const getCurrencySymbolForCountry = (countryName) => {
  const country = countryName?.toLowerCase() || '';
  
  if (country.includes('united states') || country.includes('usa') || country.includes('america')) {
    return '$';
  } else if (country.includes('united kingdom') || country.includes('uk') || country.includes('britain') || country.includes('england')) {
    return '£';
  } else if (country.includes('uae') || country.includes('united arab emirates') || country.includes('emirates')) {
    return 'د.إ';
  } else if (country.includes('saudi') || country.includes('ksa') || country.includes('arabia')) {
    return 'ر.س';
  }
  
  // Default fallback to GBP
  return '£';
};

// Helper function to get currency code for a specific country
const getCountryCurrency = (countryName) => {
  const country = countryName?.toLowerCase() || '';
  
  if (country.includes('united states') || country.includes('usa') || country.includes('america')) {
    return '$';
  } else if (country.includes('united kingdom') || country.includes('uk') || country.includes('britain') || country.includes('england')) {
    return '£';
  } else if (country.includes('uae') || country.includes('united arab emirates') || country.includes('emirates')) {
    return 'AED';
  } else if (country.includes('saudi') || country.includes('ksa') || country.includes('arabia')) {
    return 'KSA';
  }
  
  // Default fallback to GBP
  return '£';
};

function Checkout() {
  const navigate = useNavigate();
  const { items, getCartTotal, clearCart } = useCart();
  const { currency } = useCurrency();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    paymentMethod: 'stripe'
  });
  const [orderData, setOrderData] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(true);

  // Fetch shipping rates on component mount
  useEffect(() => {
    fetchShippingRates();
  }, []);

  // Auto-select country based on currency
  useEffect(() => {
    if (currency && shippingRates.length > 0) {
      let targetCountry = '';
      
      // Map currency to country
      switch (currency) {
        case '$':
          targetCountry = 'United States';
          break;
        case '£':
          targetCountry = 'United Kingdom';
          break;
        case 'AED':
          targetCountry = 'UAE';
          break;
        case 'KSA':
          targetCountry = 'Saudi Arabia';
          break;
        default:
          targetCountry = '';
      }
      
      console.log(`Currency changed to: ${currency}, target country: ${targetCountry}`);
      console.log(`Available shipping rates:`, shippingRates.map(rate => rate.country_name));
      
      if (targetCountry) {
        // Check if the target country exists in shipping rates with flexible matching
        const matchingRate = shippingRates.find(rate => {
          const rateCountry = rate.country_name.toLowerCase();
          const target = targetCountry.toLowerCase();
          
          // Check for exact match or partial matches
          return rateCountry === target ||
                 rateCountry.includes(target) ||
                 target.includes(rateCountry) ||
                 (target === 'united states' && (rateCountry.includes('usa') || rateCountry.includes('united states'))) ||
                 (target === 'united kingdom' && (rateCountry.includes('uk') || rateCountry.includes('united kingdom') || rateCountry.includes('britain'))) ||
                 (target === 'uae' && (rateCountry.includes('uae') || rateCountry.includes('united arab emirates'))) ||
                 (target === 'saudi arabia' && (rateCountry.includes('saudi') || rateCountry.includes('ksa')));
        });
        
        if (matchingRate) {
          setSelectedCountry(matchingRate.country_name);
          setSelectedShippingRate(matchingRate);
          console.log(`Auto-selected country: ${matchingRate.country_name} for currency: ${currency}`);
        } else {
          // Fallback: try to find any country that might match
          const fallbackRate = shippingRates.find(rate => {
            const rateCountry = rate.country_name.toLowerCase();
            const target = targetCountry.toLowerCase();
            
            // More flexible matching for fallback
            return rateCountry.includes(target.substring(0, 3)) || // First 3 letters
                   target.includes(rateCountry.substring(0, 3)) ||
                   (target === 'united states' && rateCountry.includes('america')) ||
                   (target === 'united kingdom' && rateCountry.includes('england')) ||
                   (target === 'uae' && rateCountry.includes('emirates')) ||
                   (target === 'saudi arabia' && rateCountry.includes('arabia'));
          });
          
          if (fallbackRate) {
            setSelectedCountry(fallbackRate.country_name);
            setSelectedShippingRate(fallbackRate);
            console.log(`Fallback selected country: ${fallbackRate.country_name} for currency: ${currency}`);
          } else {
            console.log(`No matching country found for currency: ${currency}, target: ${targetCountry}`);
          }
        }
      }
    }
  }, [currency, shippingRates]);

  const fetchShippingRates = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_rates')
        .select('*')
        .eq('is_active', true)
        .order('country_name');

      if (error) {
        console.error('Error fetching shipping rates:', error);
      } else {
        setShippingRates(data || []);
      }
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
    } finally {
      setLoadingShipping(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    setSelectedCountry(countryName);
    
    // Find the shipping rate for the selected country
    const rate = shippingRates.find(rate => rate.country_name === countryName);
    setSelectedShippingRate(rate || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setPaymentError(null);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.email || 
          !formData.address || !formData.city || !formData.state || !formData.zipCode) {
        throw new Error('Please fill in all required fields');
      }

      if (!selectedCountry) {
        throw new Error('Please select a country');
      }

      if (!selectedShippingRate) {
        throw new Error('Please select a shipping method');
      }

      if (items.length === 0) {
        throw new Error('Your cart is empty');
      }

      // Create order object for payment
      const order = {
        customer_first_name: formData.firstName.trim(),
        customer_last_name: formData.lastName.trim(),
        customer_email: formData.email.trim(),
        customer_phone: formData.phone.trim(),
        customer_address: formData.address.trim(),
        customer_city: formData.city.trim(),
        customer_state: formData.state.trim(),
        customer_zip_code: formData.zipCode.trim(),
        customer_country: selectedCountry,
        payment_method: formData.paymentMethod,
        items: items,
        subtotal: getCartTotal(),
        tax: 0,
        shipping: shipping,
        total: total,
        order_number: generateOrderNumber(),
        status: 'pending',
        payment_status: 'pending'
      };

      console.log('Order prepared for payment:', order);

      // Set order data for payment processing
      setOrderData(order);
      setIsFormValid(true);
      setIsProcessing(false);
    } catch (error) {
      console.error('Error preparing order:', error);
      setPaymentError(error.message || 'There was an error preparing your order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const generateOrderNumber = () => {
    return 'ORD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9).toUpperCase();
  };

  const subtotal = getCartTotal();
  const tax = 0;
  
  // Calculate shipping cost
  let shipping = 0;
  if (selectedShippingRate) {
    // For Saudi Arabia, free shipping threshold is stored in USD, convert to GBP for comparison
    const freeShippingThresholdInGBP = convertToGBP(selectedShippingRate.free_shipping_threshold, '$');
    
    // Debug logging
    console.log('Shipping Debug:', {
      country: selectedShippingRate.country_name,
      shippingCost: selectedShippingRate.shipping_cost,
      freeShippingThreshold: selectedShippingRate.free_shipping_threshold,
      freeShippingThresholdInGBP,
      subtotal,
      subtotalInUSD: convertPrice(subtotal, '$'),
      subtotalInKSA: convertPrice(subtotal, 'KSA')
    });
    
    // Check if order qualifies for free shipping
    if (selectedShippingRate.free_shipping_threshold > 0 && subtotal >= freeShippingThresholdInGBP) {
      shipping = 0;
    } else {
      // Use shipping cost as-is (already in correct currency)
      shipping = selectedShippingRate.shipping_cost;
    }
  }
  
  const total = subtotal + shipping;

  return (
    <div className="checkout-page">
      <div className="checkout-hero">
        <div className="checkout-hero-content">
          <h1>Checkout</h1>
          <p>Complete your purchase</p>
        </div>
      </div>

      <div className="checkout-container">

        <form 
          onSubmit={(e) => {
            // Only submit if Stripe form is not active
            if (isFormValid && orderData) {
              e.preventDefault();
              console.log('⚠️ Parent form submit prevented - Stripe form is active');
              return false;
            }
            handleSubmit(e);
          }} 
          className="checkout-form"
        >
          <div className="checkout-content">
            <div className="form-sections">
              {/* Shipping Information */}
              <div className="form-section">
                <h2>Shipping Information</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="address">Street Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="zipCode">ZIP Code *</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="country">Country *</label>
                    <select
                      id="country"
                      name="country"
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      required
                      disabled={loadingShipping}
                    >
                      <option value="">Select a country</option>
                      {shippingRates.map((rate) => (
                        <option key={rate.id} value={rate.country_name}>
                          {rate.country_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="form-section">
                <h2>Payment Details</h2>
                <div className="payment-method">
                  <div className="payment-option">
                    <input
                      type="radio"
                      id="stripe"
                      name="paymentMethod"
                      value="stripe"
                      checked={formData.paymentMethod === 'stripe'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="stripe" className="payment-label">
                      <div className="payment-icon">💳</div>
                      <div className="payment-details">
                        <h4>Credit/Debit Card</h4>
                        <p>Pay securely with Stripe</p>
                      </div>
                    </label>
                  </div>
                </div>
                
                {/* Show Stripe Payment Form directly */}
                {items.length > 0 && (
                  <div className="stripe-payment-section">
                    <ErrorBoundary>
                      <StripePayment
                        orderData={{
                          customer_first_name: formData.firstName.trim() || '',
                          customer_last_name: formData.lastName.trim() || '',
                          customer_email: formData.email.trim() || '',
                          customer_phone: formData.phone.trim() || '',
                          customer_address: formData.address.trim() || '',
                          customer_city: formData.city.trim() || '',
                          customer_state: formData.state.trim() || '',
                          customer_zip_code: formData.zipCode.trim() || '',
                          customer_country: selectedCountry || '',
                          payment_method: formData.paymentMethod,
                          items: items,
                          subtotal: getCartTotal(),
                          tax: 0,
                          shipping: shipping,
                          total: total,
                          order_number: generateOrderNumber(),
                          status: 'pending',
                          payment_status: 'pending'
                        }}
                        onPaymentSuccess={(details) => {
                          console.log('Payment successful:', details);
                        }}
                        onPaymentError={(error) => {
                          const errorMessage = error?.message || 'Payment failed. Please check your card details and try again.';
                          setPaymentError(errorMessage);
                          setTimeout(() => {
                            const errorElement = document.querySelector('.payment-error');
                            if (errorElement) {
                              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                        }}
                      />
                    </ErrorBoundary>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h2>Order Summary</h2>
              
              <div className="order-items">
                {items.map((item) => (
                  <div key={item.id} className="order-item">
                    <div className="item-info">
                      <span className="item-name">{item.name}</span>
                      <span className="item-quantity">Qty: {item.quantity}</span>
                      <span className="item-dispatch">
                        {item.vendor_id ? (
                          <span className="vendor-dispatch">Dispatch by Vendor</span>
                        ) : (
                          <span className="admin-dispatch">Dispatch by Inifiy</span>
                        )}
                      </span>
                    </div>
                    <span className="item-price">{formatPrice(item.price * item.quantity, currency)}</span>
                  </div>
                ))}
              </div>

              <div className="order-totals">
                                  <div className="total-row">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal, currency)}</span>
                  </div>
                <div className="total-row">
                  <span>Shipping</span>
                  <span>
                    {selectedShippingRate ? (
                      (() => {
                        const freeShippingThresholdInGBP = convertToGBP(selectedShippingRate.free_shipping_threshold, '$');
                        return selectedShippingRate.free_shipping_threshold > 0 && subtotal >= freeShippingThresholdInGBP ? (
                          <span className="free-shipping">Free</span>
                        ) : (
                          <span>{getCurrencySymbolForCountry(selectedShippingRate.country_name)}{parseFloat(selectedShippingRate.shipping_cost).toFixed(2)}</span>
                        );
                      })()
                    ) : (
                      <span className="select-country">Select country</span>
                    )}
                  </span>
                </div>
                <div className="total-row final">
                  <span>Total</span>
                  <span>{formatPrice(total, currency)}</span>
                </div>
              </div>

              {paymentError && (
                <div className="payment-error">
                  <p>{paymentError}</p>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Checkout; 